import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import Cookie from 'js-cookie';
import get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';
import { toGlobalId } from 'graphql-relay';

import PageTitle from '../../components/PageTitle';
import Sidebar from '../../components/Sidebar';
import { updateNotificationCount } from '../Notifications/actions';
import UpgradeForm from '../UpgradeForm';
import CommunityAvailabilitySidebar from '../CommunityAvailabilitySidebar';
import Toast from '../../components/Toast';

import './styles.css';

const messages = defineMessages({
  frameTitle: {
    id: 'community.frame.title',
    defaultMessage: 'Community',
  },
  successDescription: {
    id: 'repvisits.travel.plan.successDescription',
    defaultMessage: 'School added to Travel Plan',
  },
  failureDescription: {
    id: 'repvisits.travel.plan.failureDescription',
    defaultMessage: 'Something unexpected happened. Please try your request again.',
  },
});

function getCurrentDomain() {
  return window.location.hostname.split('.').slice(-2).join('.');
}

function getSplatFromRouter(router) {
  return router.params.splat + router.location.search + router.location.hash;
}

function setAuthCookie(viewer) {
  const secure = window.location.protocol === 'https:';
  const domain = getCurrentDomain();
  Cookie.set('auth', viewer.jwtToken, { domain, secure });
}

class CommunityComponent extends React.Component {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        jwtToken: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    updateNotificationCount: PropTypes.func.isRequired,
    notifications: PropTypes.shape({
      count: PropTypes.number.isRequired,
    }).isRequired,
    router: PropTypes.shape({
      replace: PropTypes.func.isRequired,
      params: PropTypes.object.isRequired,
    }).isRequired,
    viewer: PropTypes.object,
    addTravelPlan: PropTypes.func.isRequired,
  };

  successNotification =
    <Toast
      active
      title=""
      kind="success"
      description={this.props.intl.formatMessage(messages.successDescription)}
      onDeactivate={() => this.setState({ toastNotification: null })}
    />;

  failureNotification =
    <Toast
      active
      title=""
      kind="error"
      description={this.props.intl.formatMessage(messages.failureDescription)}
      onDeactivate={() => this.setState({ toastNotification: null })}
    />;

  constructor(...args) {
    super(...args);
    const { router, current } = this.props;
    this.documentTitle = document.title;
    /* istanbul ignore if */
    if (window.self === window.top) { // without this, karma tests timeout..... huuuuhhh???
      try {
        document.domain = getCurrentDomain();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    }

    setAuthCookie(current.viewer);

    this.iframeUniqueId = uniqueId('community_');
    const splatFromRouter = getSplatFromRouter(router);
    this.state = {
      routerSplat: splatFromRouter,
      iframeSplat: splatFromRouter,
      openModal: false,
      availabilitySidebarSchoolId: null,
      toastNotification: null,
    };
  }

  componentWillReceiveProps(nextProps) {
    setAuthCookie(nextProps.current.viewer);
    this.setState({
      routerSplat: getSplatFromRouter(nextProps.router),
    });
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState.routerSplat !== nextState.iframeSplat) {
      this.iframeUniqueId = uniqueId('community_');
      return true;
    }
    // only rerender if iframe is forced or if modal or sidebar visibility changes or changing toastNotification
    return nextState.openModal !== this.state.openModal ||
      nextState.availabilitySidebarSchoolId !== this.state.availabilitySidebarSchoolId ||
      nextState.toastNotification !== this.state.toastNotification;
  }

  componentWillUnmount() {
    document.title = this.documentTitle;
    if (this.iframeNode) {
      this.iframeNode.removeEventListener('load', this.onLoad);
    }
  }

  onLoad = () => {
    const { router } = this.props;
    const { iframeSplat } = this.state;

    const iframeLocation = get(this.iframeNode, 'contentWindow.location');
    /* istanbul ignore if */
    if (!iframeLocation) {
      console.error('Unable to read iframe location, integration disabled'); // eslint-disable-line no-console
      return;
    }
    const currentIframeSplat = iframeLocation.pathname.slice(1) + iframeLocation.search + iframeLocation.hash;
    if (iframeSplat !== currentIframeSplat) {
      this.setState({ routerSplat: currentIframeSplat, iframeSplat: currentIframeSplat });
    }
    router.replace(`/counselor-community/${currentIframeSplat}`);
    document.title = this.iframeNode.contentDocument.title;
    this.updateNotificationCount(this.iframeNode.contentDocument);
    this._addEventListeners(this.iframeNode.contentDocument.body);
    this._patchAjax(this.iframeNode.contentWindow.XMLHttpRequest);
  };

  _patchAjax(request) {
    const originalSend = request.prototype.send;
    const self = this;
    request.prototype.send = function sendPatch(...args) {
      self._updateCookie();
      return originalSend.apply(this, args);
    };
  }

  _addEventListeners(element) {
    element.addEventListener('click', this._updateCookie);
    element.addEventListener('submit', this._updateCookie);
    element.addEventListener('click', this._checkIfUpgradeModalWasRequested);
    element.addEventListener('click', this._checkIfAvailabilitySidebarWasRequested);
    element.addEventListener('click', this._checkIfAddTravelPlanWasRequested);
  }

  _checkIfUpgradeModalWasRequested = (e) => {
    if ((e.target.dataset && e.target.dataset.upgradeModal === 'true') ||
      e.target.getAttribute('data-upgrade-modal') === 'true'
    ) {
      e.preventDefault();
      this.showModal();
    }
  };

  _checkIfAddTravelPlanWasRequested = (e) => {
    if ((e.target.dataset || e.target.getAttribute('data-hs-id')) && e.target.getAttribute('data-add-travel-plan') === 'true') {
      e.preventDefault();
      const schoolId = (e.target.dataset && e.target.dataset.hsId) || e.target.getAttribute('data-hs-id');
      this.addTravelPlan(schoolId);
    }
  };

  _checkIfAvailabilitySidebarWasRequested = (e) => {
    if ((e.target.dataset && e.target.dataset.availabilitySidebar === 'true') ||
      e.target.getAttribute('data-availability-sidebar') === 'true'
    ) {
      e.preventDefault();
      const schoolId = (e.target.dataset && e.target.dataset.hsId) || e.target.getAttribute('data-hs-id');
      this.showAvailability(schoolId);
    } else if (this.state.availabilitySidebarSchoolId !== null) {
      e.preventDefault();
      this.hideAvailability();
    }
  };

  _updateCookie = () => {
    const { current } = this.props;
    setAuthCookie(current.viewer);
  };

  updateNotificationCount(doc) {
    const { notifications } = this.props;
    const meta = doc.querySelector('meta[name=notifications]');
    if (meta) {
      const count = parseInt(meta.getAttribute('content'), 10) || 0;
      if (count !== notifications.count) {
        this.props.updateNotificationCount(count);
      }
    }
  }

  iframeMounted = (ref) => {
    if (ref) {
      if (this.iframeNode) {
        this.iframeNode.removeEventListener('load', this.onLoad);
      }
      this.iframeNode = ref;
      ref.addEventListener('load', this.onLoad);
    }
  };

  closeModal = () => {
    this.setState({ openModal: false });
  };

  showModal = () => {
    this.setState({ openModal: true });
  };

  hideAvailability = () => {
    this.setState({ availabilitySidebarSchoolId: null });
  };

  showAvailability = (schoolId) => {
    this.setState({ availabilitySidebarSchoolId: schoolId });
  };

  addTravelPlan = (schoolId) => {
    const input = { highSchoolId: toGlobalId('HighSchool', schoolId) };
    this.props.addTravelPlan({ variables: { input } })
      .then(this.handleMutationSuccess)
      .catch(/* istanbul ignore next */ this.handleMutationFailure);
  };

  handleMutationFailure = () => {
    this.handleTravelPlanCallback(false);
  };

  handleMutationSuccess = () => {
    this.handleTravelPlanCallback(true);
  };

  handleTravelPlanCallback = (success) => {
    if (!success) {
      this.setState({ toastNotification: this.failureNotification });
    } else {
      this.setState({ toastNotification: this.successNotification });
    }
  };

  render() {
    const { intl } = this.props;
    const { routerSplat, openModal, availabilitySidebarSchoolId, toastNotification } = this.state;
    return (
      <div styleName="wrapper">
        <Sidebar.Pushable styleName="pushable">
          <Sidebar.Pusher>
            <PageTitle title="Counselor Community" id="pageTitle" shortTitle="Community" category="Awareness" />
            <iframe
              key={this.iframeUniqueId}
              title={intl.formatMessage(messages.frameTitle)}
              frameBorder="0"
              styleName="iframe"
              src={`${process.env.COMMUNITY_URL}${routerSplat}`}
              ref={this.iframeMounted}
              id="content"
              aria-label="Counselor Community"
            />
            <UpgradeForm open={openModal} onClose={this.closeModal} />
          </Sidebar.Pusher>
          <CommunityAvailabilitySidebar
            visible={availabilitySidebarSchoolId !== null}
            closeFunction={this.hideAvailability}
            schoolId={availabilitySidebarSchoolId}
          />
        </Sidebar.Pushable>
        {toastNotification}
      </div>

    );
  }
}

const mapStateToProps = (state) => ({ current: state.current, notifications: state.notifications });

const IntlCommunityComponent = injectIntl(CommunityComponent);

export default connect(mapStateToProps, { updateNotificationCount })(IntlCommunityComponent);
